import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, TextField, Button, Typography, InputAdornment, IconButton, Alert, CircularProgress, Chip } from '@mui/material';
import { Visibility, VisibilityOff, MonitorHeart, LocalHospital, People, CalendarMonth, Inventory2 } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  const features = [
    { icon: <People sx={{ fontSize: 20, color: '#0d9488' }} />, text: 'Patient Management' },
    { icon: <CalendarMonth sx={{ fontSize: 20, color: '#6366f1' }} />, text: 'Appointment Scheduling' },
    { icon: <Inventory2 sx={{ fontSize: 20, color: '#f59e0b' }} />, text: 'Inventory Control' },
    { icon: <LocalHospital sx={{ fontSize: 20, color: '#ec4899' }} />, text: 'Multi-Hospital Support' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f0fdfa' }}>

      {/* Left Panel */}
      <Box sx={{ flex: 1, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', justifyContent: 'center', px: 8, background: 'linear-gradient(145deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)', position: 'relative', overflow: 'hidden' }}>
        {/* Background circles */}
        <Box sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', top: -100, right: -100 }} />
        <Box sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', bottom: -80, left: -80 }} />

        <Box sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)' }}>
              <MonitorHeart sx={{ fontSize: 30, color: 'white' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'white', lineHeight: 1 }}>MedelaHub</Typography>
              <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Hospital Management System</Typography>
            </Box>
          </Box>

          <Typography sx={{ fontSize: 40, fontWeight: 800, color: 'white', lineHeight: 1.15, mb: 2 }}>
            Complete Hospital<br />Management<br />Platform
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 15, mb: 5, lineHeight: 1.7 }}>
            Manage patients, appointments, inventory, and billing — all in one powerful, modern platform built for healthcare.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {features.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.8, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2.5, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {f.icon}
                </Box>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: 14 }}>{f.text}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right Panel */}
      <Box sx={{ width: { xs: '100%', lg: 480 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: 'white', boxShadow: '-4px 0 30px rgba(0,0,0,0.04)' }}>
        <Box sx={{ width: '100%', maxWidth: 380 }}>

          {/* Mobile Logo */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2.5, background: 'linear-gradient(135deg, #0d9488, #14b8a6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MonitorHeart sx={{ fontSize: 24, color: 'white' }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: '#0f172a' }}>MedelaHub HMS</Typography>
          </Box>

          <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#0f172a', mb: 0.8 }}>Welcome back 👋</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14, mb: 3 }}>Sign in to your HMS account to continue</Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2.5 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 0.8 }}>Email Address</Typography>
            <TextField
              fullWidth type="email" value={email} onChange={e => setEmail(e.target.value)} required
              placeholder="Enter your email address"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc', '&.Mui-focused fieldset': { borderColor: '#0d9488' } } }}
            />

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 0.8 }}>Password</Typography>
            <TextField
              fullWidth type={showPass ? 'text' : 'password'} value={password}
              onChange={e => setPassword(e.target.value)} required placeholder="Enter your password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass(!showPass)} size="small">
                      {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc', '&.Mui-focused fieldset': { borderColor: '#0d9488' } } }}
            />

            <Button type="submit" fullWidth variant="contained" disabled={loading} size="large"
              sx={{ py: 1.5, borderRadius: 2.5, background: 'linear-gradient(135deg, #0d9488, #0f766e)', fontWeight: 700, fontSize: 15, textTransform: 'none', boxShadow: '0 4px 20px rgba(13,148,136,0.4)', '&:hover': { background: 'linear-gradient(135deg, #0f766e, #115e59)' } }}>
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Sign In'}
            </Button>
          </form>

          {/* Demo Credentials */}
          <Box sx={{ mt: 3.5, p: 2.5, bgcolor: '#f0fdfa', borderRadius: 2.5, border: '1px solid #99f6e4' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488', mb: 1.5 }}>🔑 Demo Credentials</Typography>
            {[
              { role: 'Super Admin', email: 'superadmin@hms.com', pass: 'Admin@123', color: '#0d9488' },
              { role: 'Hospital Admin', email: 'admin@cityhospital.com', pass: 'Admin@123', color: '#6366f1' },
              { role: 'Doctor', email: 'doctor@cityhospital.com', pass: 'Doctor@123', color: '#f59e0b' },
            ].map(c => (
              <Box key={c.role} onClick={() => { setEmail(c.email); setPassword(c.pass); }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: '#ccfbf1' }, mb: 0.8, transition: 'background 0.15s' }}>
                <Chip label={c.role} size="small" sx={{ bgcolor: `${c.color}18`, color: c.color, fontWeight: 700, fontSize: 10, height: 20 }} />
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>{c.email}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
